/**
 * TabsExamples.tsx
 * Comprehensive examples of HeroUI Tabs component
 * 
 * Showcases all HeroUI Tabs features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Tabs, Tab } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { 
  BarChart3,
  Settings,
  TrendingUp,
  FileText,
  PieChart,
  Target,
  Home,
  Activity,
  Shield,
  Zap,
  LineChart,
  Search
} from 'lucide-react';

export const TabsExamples: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<string>('dashboard');

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Tabs Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tabs Variants</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Solid Variant (Default) */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Solid (Default)</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="solid">
                  <Tab key="trades" title="Trades">
                    <div className="py-4">
                      <p>Your trading history and active positions</p>
                      <div className="mt-2 flex gap-2">
                        <Badge color="success">5 Winners</Badge>
                        <Badge color="danger">2 Losers</Badge>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="analytics" title="Analytics">
                    <div className="py-4">
                      <p>Performance metrics and detailed analysis</p>
                      <div className="mt-2">
                        <Badge color="primary">Win Rate: 71%</Badge>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="settings" title="Settings">
                    <div className="py-4">
                      <p>Configure your trading preferences</p>
                    </div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Bordered Variant */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Bordered</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="bordered">
                  <Tab key="dashboard" title="Dashboard">
                    <div className="py-4">
                      <p>Overview of your trading performance</p>
                      <div className="mt-3 space-y-2">
                        <div className="flex justify-between">
                          <span>Total P&L:</span>
                          <span className="text-green-600 font-medium">+$2,450</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Active Trades:</span>
                          <span>3</span>
                        </div>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="journal" title="Journal">
                    <div className="py-4">
                      <p>Trading journal and notes</p>
                    </div>
                  </Tab>
                  <Tab key="reports" title="Reports">
                    <div className="py-4">
                      <p>Generate and view trading reports</p>
                    </div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Light Variant */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Light</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="light">
                  <Tab key="overview" title="Overview">
                    <div className="py-4">
                      <p>Quick overview of key metrics</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <Chip size="sm" color="primary">Equity: $45K</Chip>
                        <Chip size="sm" color="secondary">Margin: $5K</Chip>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="positions" title="Positions">
                    <div className="py-4">
                      <p>Current market positions</p>
                    </div>
                  </Tab>
                  <Tab key="history" title="History">
                    <div className="py-4">
                      <p>Historical trading data</p>
                    </div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Underlined Variant */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Underlined</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="underlined">
                  <Tab key="watchlist" title="Watchlist">
                    <div className="py-4">
                      <p>Symbols you're monitoring</p>
                      <div className="mt-2 flex gap-1 flex-wrap">
                        <Chip size="sm" variant="flat">AAPL</Chip>
                        <Chip size="sm" variant="flat">MSFT</Chip>
                        <Chip size="sm" variant="flat">GOOGL</Chip>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="alerts" title="Alerts">
                    <div className="py-4">
                      <p>Price alerts and notifications</p>
                    </div>
                  </Tab>
                  <Tab key="research" title="Research">
                    <div className="py-4">
                      <p>Market research and analysis</p>
                    </div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tab Colors</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Primary Color */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Primary Color</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="bordered" color="primary">
                  <Tab key="primary-1" title="Trading">
                    <div className="py-3">Primary colored tabs for main features</div>
                  </Tab>
                  <Tab key="primary-2" title="Analysis">
                    <div className="py-3">Market analysis tools</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Secondary Color */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Secondary Color</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="bordered" color="secondary">
                  <Tab key="secondary-1" title="Portfolio">
                    <div className="py-3">Secondary colored tabs for supporting features</div>
                  </Tab>
                  <Tab key="secondary-2" title="Performance">
                    <div className="py-3">Performance tracking</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Success Color */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Success Color</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="bordered" color="success">
                  <Tab key="success-1" title="Profits">
                    <div className="py-3">Profitable trades and positive metrics</div>
                  </Tab>
                  <Tab key="success-2" title="Winners">
                    <div className="py-3">Winning strategies</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Warning Color */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Warning Color</h4>
              </CardHeader>
              <CardBody>
                <Tabs variant="bordered" color="warning">
                  <Tab key="warning-1" title="Risks">
                    <div className="py-3">Risk management and warnings</div>
                  </Tab>
                  <Tab key="warning-2" title="Alerts">
                    <div className="py-3">Important notifications</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tab Sizes</h3>
          <div className="space-y-6">
            
            {/* Small Size */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Small Size</h4>
              </CardHeader>
              <CardBody>
                <Tabs size="sm" variant="bordered">
                  <Tab key="sm-1" title="Compact">
                    <div className="py-3">Small tabs for compact layouts</div>
                  </Tab>
                  <Tab key="sm-2" title="Space Efficient">
                    <div className="py-3">Perfect for sidebars and tight spaces</div>
                  </Tab>
                  <Tab key="sm-3" title="Minimal">
                    <div className="py-3">Minimal footprint</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Medium Size (Default) */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Medium Size (Default)</h4>
              </CardHeader>
              <CardBody>
                <Tabs size="md" variant="bordered">
                  <Tab key="md-1" title="Standard">
                    <div className="py-3">Standard size tabs for most use cases</div>
                  </Tab>
                  <Tab key="md-2" title="Balanced">
                    <div className="py-3">Good balance of size and functionality</div>
                  </Tab>
                  <Tab key="md-3" title="Default">
                    <div className="py-3">Default recommended size</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Large Size */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Large Size</h4>
              </CardHeader>
              <CardBody>
                <Tabs size="lg" variant="bordered">
                  <Tab key="lg-1" title="Prominent">
                    <div className="py-3">Large tabs for primary navigation</div>
                  </Tab>
                  <Tab key="lg-2" title="Accessible">
                    <div className="py-3">Better touch targets and visibility</div>
                  </Tab>
                  <Tab key="lg-3" title="Hero">
                    <div className="py-3">Perfect for hero sections</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Full Width */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Full Width Tabs</h3>
          <Card>
            <CardHeader>
              <h4 className="font-medium text-text-primary">Full Width Layout</h4>
            </CardHeader>
            <CardBody>
              <Tabs fullWidth variant="underlined" color="primary">
                <Tab key="full-1" title="Portfolio Overview">
                  <div className="py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">+12.5%</p>
                        <p className="text-sm text-text-secondary">Total Return</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-text-primary">$125,430</p>
                        <p className="text-sm text-text-secondary">Portfolio Value</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">23</p>
                        <p className="text-sm text-text-secondary">Total Positions</p>
                      </div>
                    </div>
                  </div>
                </Tab>
                <Tab key="full-2" title="Active Trades">
                  <div className="py-4">
                    <p>Currently active trading positions and their performance</p>
                  </div>
                </Tab>
                <Tab key="full-3" title="Market Analysis">
                  <div className="py-4">
                    <p>Technical and fundamental analysis of market conditions</p>
                  </div>
                </Tab>
              </Tabs>
            </CardBody>
          </Card>
        </div>

        {/* Tab Placement */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tab Placement</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Top Placement (Default) */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Top (Default)</h4>
              </CardHeader>
              <CardBody>
                <Tabs placement="top" variant="bordered">
                  <Tab key="top-1" title="Dashboard">
                    <div className="py-3">Tabs at the top (standard layout)</div>
                  </Tab>
                  <Tab key="top-2" title="Settings">
                    <div className="py-3">Most common placement</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Bottom Placement */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Bottom</h4>
              </CardHeader>
              <CardBody>
                <Tabs placement="bottom" variant="bordered">
                  <Tab key="bottom-1" title="Navigation">
                    <div className="py-3">Tabs at the bottom (mobile-style)</div>
                  </Tab>
                  <Tab key="bottom-2" title="Actions">
                    <div className="py-3">Good for mobile interfaces</div>
                  </Tab>
                </Tabs>
              </CardBody>
            </Card>

            {/* Start Placement (Vertical) */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">Start (Vertical Left)</h4>
              </CardHeader>
              <CardBody>
                <div className="min-h-[200px]">
                  <Tabs placement="start" variant="bordered" isVertical>
                    <Tab key="start-1" title="Profile">
                      <div className="py-3 pl-4">Vertical tabs on the left</div>
                    </Tab>
                    <Tab key="start-2" title="Security">
                      <div className="py-3 pl-4">Good for sidebar navigation</div>
                    </Tab>
                    <Tab key="start-3" title="Notifications">
                      <div className="py-3 pl-4">Settings-style layout</div>
                    </Tab>
                  </Tabs>
                </div>
              </CardBody>
            </Card>

            {/* End Placement (Vertical) */}
            <Card>
              <CardHeader>
                <h4 className="font-medium text-text-primary">End (Vertical Right)</h4>
              </CardHeader>
              <CardBody>
                <div className="min-h-[200px]">
                  <Tabs placement="end" variant="bordered" isVertical>
                    <Tab key="end-1" title="Charts">
                      <div className="py-3 pr-4">Vertical tabs on the right</div>
                    </Tab>
                    <Tab key="end-2" title="Indicators">
                      <div className="py-3 pr-4">Alternative layout option</div>
                    </Tab>
                    <Tab key="end-3" title="Strategies">
                      <div className="py-3 pr-4">Unique visual approach</div>
                    </Tab>
                  </Tabs>
                </div>
              </CardBody>
            </Card>

          </div>
        </div>

        {/* Tabs with Icons */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Tabs with Icons</h3>
          <Card>
            <CardHeader>
              <h4 className="font-medium text-text-primary">Icon + Text Tabs</h4>
            </CardHeader>
            <CardBody>
              <Tabs variant="light" color="primary">
                <Tab 
                  key="home" 
                  title={
                    <div className="flex items-center space-x-2">
                      <Home size={16} />
                      <span>Home</span>
                    </div>
                  }
                >
                  <div className="py-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      <span>Dashboard Overview</span>
                    </div>
                    <p>Your trading dashboard with key metrics and recent activity.</p>
                  </div>
                </Tab>
                
                <Tab 
                  key="trades" 
                  title={
                    <div className="flex items-center space-x-2">
                      <TrendingUp size={16} />
                      <span>Trades</span>
                    </div>
                  }
                >
                  <div className="py-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      <span>Trading Activity</span>
                    </div>
                    <p>View and manage your trading positions and history.</p>
                    <div className="flex gap-2">
                      <Button size="sm" startContent={<LineChart className="w-3 h-3" />}>
                        View Charts
                      </Button>
                      <Button size="sm" variant="bordered" startContent={<Search className="w-3 h-3" />}>
                        Search Trades
                      </Button>
                    </div>
                  </div>
                </Tab>
                
                <Tab 
                  key="analytics" 
                  title={
                    <div className="flex items-center space-x-2">
                      <PieChart size={16} />
                      <span>Analytics</span>
                    </div>
                  }
                >
                  <div className="py-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span>Performance Metrics</span>
                    </div>
                    <p>Detailed analytics and performance tracking.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Badge color="success" variant="flat">Win Rate: 68%</Badge>
                      <Badge color="primary" variant="flat">Profit Factor: 1.8</Badge>
                      <Badge color="warning" variant="flat">Max DD: -5.2%</Badge>
                      <Badge color="secondary" variant="flat">Sharpe: 1.45</Badge>
                    </div>
                  </div>
                </Tab>
                
                <Tab 
                  key="settings" 
                  title={
                    <div className="flex items-center space-x-2">
                      <Settings size={16} />
                      <span>Settings</span>
                    </div>
                  }
                >
                  <div className="py-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      <span>Configuration</span>
                    </div>
                    <p>Application settings and preferences.</p>
                    <Button size="sm" startContent={<Zap className="w-3 h-3" />}>
                      Manage Settings
                    </Button>
                  </div>
                </Tab>
              </Tabs>
            </CardBody>
          </Card>
        </div>

        {/* Controlled Tabs */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Controlled Tabs</h3>
          <Card>
            <CardHeader>
              <h4 className="font-medium text-text-primary">Programmatic Tab Control</h4>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    color="primary"
                    variant={selectedTab === 'dashboard' ? 'solid' : 'bordered'}
                    onPress={() => setSelectedTab('dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                  <Button 
                    size="sm" 
                    color="secondary"
                    variant={selectedTab === 'portfolio' ? 'solid' : 'bordered'}
                    onPress={() => setSelectedTab('portfolio')}
                  >
                    View Portfolio
                  </Button>
                  <Button 
                    size="sm" 
                    color="success"
                    variant={selectedTab === 'reports' ? 'solid' : 'bordered'}
                    onPress={() => setSelectedTab('reports')}
                  >
                    Show Reports
                  </Button>
                </div>
                
                <Tabs 
                  selectedKey={selectedTab}
                  onSelectionChange={(key) => setSelectedTab(key as string)}
                  variant="underlined"
                  color="primary"
                >
                  <Tab key="dashboard" title="Dashboard">
                    <div className="py-4 space-y-3">
                      <h5 className="font-medium">Trading Dashboard</h5>
                      <p>Real-time overview of your trading performance and active positions.</p>
                      <div className="flex gap-2">
                        <Badge color="success">Active: 3</Badge>
                        <Badge color="primary">P&L: +$1,240</Badge>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab key="portfolio" title="Portfolio">
                    <div className="py-4 space-y-3">
                      <h5 className="font-medium">Portfolio Management</h5>
                      <p>Manage your trading portfolio and asset allocation.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Chip size="sm">Stocks: 60%</Chip>
                        <Chip size="sm">Options: 25%</Chip>
                        <Chip size="sm">Futures: 10%</Chip>
                        <Chip size="sm">Cash: 5%</Chip>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab key="reports" title="Reports">
                    <div className="py-4 space-y-3">
                      <h5 className="font-medium">Performance Reports</h5>
                      <p>Generate detailed reports of your trading performance.</p>
                      <Button size="sm" startContent={<FileText className="w-3 h-3" />}>
                        Generate Report
                      </Button>
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};